import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket } from "lucide-react";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { pick } from "../i18n/strings";

export default function Trips() {
  const { t, lang } = useLang();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/bookings/me")
      .then((res) => setBookings(res.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-6 md:px-12 pt-7 md:pt-12 mx-auto max-w-3xl" data-testid="trips-page">
      <div className="flex items-center justify-between md:hidden">
        <Logo />
        <LanguageSwitcher />
      </div>

      <header className="mt-9 md:mt-4">
        <p className="text-[10px] tracking-[0.45em] uppercase font-bold text-brand-tan">
          {t("trips")}
        </p>
        <h1 className="font-serif text-4xl md:text-6xl text-brand-brown leading-[1.05] mt-3">
          {t("my_trips")}
        </h1>
      </header>

      <div className="mt-8 stagger pb-10">
        {loading ? null : bookings.length === 0 ? (
          <div className="text-center pt-16">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-ivory text-brand-tan">
              <Ticket size={22} strokeWidth={1.25} />
            </div>
            <p className="mt-6 text-brand-brown/70" data-testid="trips-empty">
              {t("no_trips")}
            </p>
            <Link
              to="/home"
              className="mt-6 inline-flex items-center justify-center px-6 py-3 rounded-2xl bg-brand-brown text-brand-cream font-bold tracking-[0.18em] uppercase text-xs"
            >
              {t("explore")}
            </Link>
          </div>
        ) : (
          bookings.map((b) => (
            <Link
              key={b.id}
              to={`/confirmation/${b.id}`}
              data-testid={`trip-${b.id}`}
              className="block mb-5 rounded-2xl overflow-hidden border border-brand-tan/25 bg-white/60 hover:bg-white transition-colors"
            >
              <div className="flex">
                <img
                  src={b.experience_snapshot.image}
                  alt=""
                  className="w-28 h-28 object-cover"
                />
                <div className="p-4 flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-tan">
                      {new Date(b.experience_snapshot.date).toLocaleDateString(
                        lang === "ca" ? "ca-ES" : lang === "en" ? "en-GB" : "es-ES",
                        { day: "2-digit", month: "short", year: "numeric" },
                      )}
                    </p>
                    <span
                      className={`text-[9px] tracking-[0.25em] uppercase font-bold px-2 py-0.5 rounded-full ${
                        b.status === "confirmed"
                          ? "bg-brand-green/15 text-brand-green"
                          : "bg-brand-gold/15 text-brand-gold"
                      }`}
                    >
                      {b.status === "confirmed" ? "OK" : "···"}
                    </span>
                  </div>
                  <p className="font-serif text-xl text-brand-brown leading-tight mt-1 truncate">
                    {pick(b.experience_snapshot.title, lang)}
                  </p>
                  <p className="text-xs text-brand-tan tracking-wider uppercase mt-1 truncate">
                    {b.experience_snapshot.location}
                  </p>
                  <p className="text-sm font-bold text-brand-brown mt-1">
                    {b.guests} {t("guests")} · €{b.total_eur}
                  </p>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
